import { useContext, useState } from 'react';
import { useLocation } from 'react-router';

import { MDText } from 'i18n-react';

import { UserContext } from 'Context/user';
import { useRemoveUser } from 'Context/useRemoveUser';

import { SP_HEADER_EVENTS } from 'Utilities/analytics/analyticsHeader';
import {
  CAREERS_URL,
  CONTACT_URL,
  HELP_URL,
  PRO_URL,
} from 'Utilities/urls';
import { MOT_VALUATION_ROUTE, TAX_VALUATION_ROUTE, ULEZ_PAGE_ROUTES, VALUATION_TRACKER_ROUTES, WHY_MOTORWAY_PATH } from 'Utilities/wordpress';

import { useBreakpoint } from '../../../components/misc/Breakpoints/Breakpoints';
import { routes } from '../../../routes';

import { handleLogoutClick } from './AccountLinks.helpers';
import LocalTexts from './HeaderTexts.json';

const LocalT = new MDText(LocalTexts);
const t = (key: string) => LocalT.translate(key) as string;

export type SubRouteType = {
  caption?: string;
  icon?: string;
  isNewRoute?: boolean;
  label: string;
  path: string;
  trackingPath?: string;
};

export type SubRoutesArray = SubRouteType[];

export type RouteType = {
  label: string;
  name?: string;
  nav?: boolean;
  path: string;
  subRoutes?: SubRoutesArray;
  trackingPath?: string;
};

export type NavRoute = Pick<RouteType, 'label' | 'name' | 'nav' | 'path' | 'subRoutes' | 'trackingPath'>;

const newTab = 'new_tab';

const isExternalLink = (route: string) => /^https?:\/\//.test(route);
const formatTrackingName = (name: string) => name.replace(/ /g, '_').toLowerCase();

// eslint-disable-next-line consistent-return
const fireTrackingEventForLink = (path: string, name: string) => {
  switch (path) {
  case CAREERS_URL:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ label: newTab, name: 'careers', url: CAREERS_URL });
    break;

  case CONTACT_URL:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ label: newTab, name: 'contact_us', url: CONTACT_URL });
    break;

  case HELP_URL:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ label: newTab, name: 'help', url: HELP_URL });
    break;

  case PRO_URL:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ label: newTab, name: 'mw_for_dealers', url: PRO_URL });
    break;

  case VALUATION_TRACKER_ROUTES[0]:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: 'cvt', url: VALUATION_TRACKER_ROUTES[0] });
    break;

  case ULEZ_PAGE_ROUTES[0]:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: 'ulez', url: ULEZ_PAGE_ROUTES[0] });
    break;

  case MOT_VALUATION_ROUTE:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: 'mot', url: MOT_VALUATION_ROUTE });
    break;

  case TAX_VALUATION_ROUTE:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: 'tax_check', url: TAX_VALUATION_ROUTE });
    break;

  case WHY_MOTORWAY_PATH:
    SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: 'why_motorway', url: WHY_MOTORWAY_PATH });
    break;

  default: return SP_HEADER_EVENTS.HEADER_LINK_CLICKED({ name: formatTrackingName(name), url: path });
  }
};

const fireMenuHoverTracking = (message: string, name: string = '') =>
  SP_HEADER_EVENTS.HEADER_LINK_HOVERED({ message: `${message} menu`, name });

export const useHeaderNav = () => {
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const breakpoints = useBreakpoint();
  const isMobile = breakpoints.maxWidth.breakpointTablet;

  const { pathname } = useLocation();

  const { userState: user } = useContext(UserContext);
  const isUser = Number.isInteger(user?.id);
  const { removeUser } = useRemoveUser();

  const navRoutes: NavRoute[] = (routes as RouteType[]).filter((route) => route.nav);

  if (pathname !== '/') {
    navRoutes.unshift({
      label: t('navigation.sellMyCar'),
      nav: true,
      path: '/',
    });
  }

  const toggleGroup = (label: string) => setActiveGroup(activeGroup === label ? null : label);

  const handleOnLogOutClick = (e: React.MouseEvent) => {
    handleLogoutClick(e);
    removeUser();
  };

  return {
    activeGroup,
    fireMenuHoverTracking,
    fireTrackingEventForLink,
    handleOnLogOutClick,
    isExternalLink,
    isMobile,
    isUser,
    menuOpen,
    navRoutes,
    setActiveGroup,
    setMenuOpen,
    toggleGroup,
  };
};
